import { Component, OnInit, ViewChild, ElementRef, Input } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Params } from '@angular/router';
import { BsModalRef } from 'ngx-bootstrap';
import { ThemeChangerService } from 'src/app/service/theme-change.service';

@Component({
  selector: 'app-affordability-calculator',
  templateUrl: './affordability-calculator.component.html',
  styleUrls: ['./affordability-calculator.component.scss']
})
export class AffordabilityCalculatorComponent implements OnInit {
  @ViewChild('donutChart', { static: true }) donutChart!: ElementRef<HTMLCanvasElement>;

  @Input() refId: string;
  @Input() showPersonalizedQuote = true;
  @Input() lpSupportEmail = '';

  title: string;
  description: string;

  calculationMethod = 'dti';
  dtiPercentage = '0.38';
  loading = false;

  calculatorForm: FormGroup;
  maxHomePrice: number = 0;
  totalMonthlyPayment: number = 0;
  sliderPercentages = {
    monthlyPayment: 0,
    annualIncome: 0,
    monthlyExpenses: 0,
    downPayment: 0
  };

  paymentBreakdown = {
    principalAndInterest: 0,
    monthlyExpenses: 0,
    propertyTax: 0,
    homeInsurance: 0
  };

  private readonly PROPERTY_TAX_RATE = 0.009; // Constant rate for property tax
  private readonly HOME_INSURANCE_RATE = 0.45; // Constant rate for home insurance

  public doughnutChartData: number[] = [];

  constructor(
    public bsModalRef: BsModalRef,
    private fb: FormBuilder,
    private activatedRoute: ActivatedRoute,
    protected themeChangerService: ThemeChangerService
  ) {
    this.activatedRoute.queryParams.subscribe((params: Params) => {
      this.title = params.title ? params.title : 'Affordability Calculator';
      this.description = params.description ? params.description : 'Find out how much home you can afford with our Affordability Estimator. Simply enter your income and expenses details to get a clear picture of your loan budget.';
      const primary = params.primary ? params.primary : '';
      const secondary = params.secondary ? params.secondary : '';
      const bgColor = params.bgColor ? params.bgColor : '';
      this.themeChangerService.changeTheme(
        primary,
        secondary,
        bgColor
      );
      if (params['refId']) {
        this.refId = params['refId'];
      }
      if (params['showPersonalizedQuote']) {
        this.showPersonalizedQuote = params['showPersonalizedQuote'] === 'true';
      }
      if (params['lpSupportEmail']) {
        this.lpSupportEmail = params['lpSupportEmail'];
      } 
    });
    this.calculatorForm = this.fb.group({
      calculationMethod: ['dti'],
      dtiPercentage: ['0.38'],
      monthlyPayment: [{ value: 1000, disabled: true }],
      annualIncome: [100000],
      monthlyExpenses: [500],
      downPayment: [20000],
      zipCode: [''],
      interestRate: [6.250],
      loanTerm: ['15 year - Fixed'],
      propertyTax: [null], // Initial value for property tax
      homeInsurance: [null] // Initial value for home insurance
    });
  }

  ngOnInit() {
    this.calculateAffordability();
    this.updateChartData();
    this.calculatorForm.valueChanges.subscribe(() => {
      this.calculateAffordability();
      this.updateChartData();
    });
  }

  onCalculationMethodChange() {
    if (this.calculationMethod === 'dti') {
      this.calculatorForm.get('monthlyPayment').disable();
    } else {
      this.calculatorForm.get('monthlyPayment').enable();
    }
    this.calculateAffordability();
  }

  onDtiPercentageChange() {
    this.calculateAffordability();
  }

  updateSliderPercentages = (event: any, controlName: string) => {
    const value = event;
    this.calculatorForm.get(controlName).setValue(value);
    const values = this.calculatorForm.value;
    this.sliderPercentages = {
      monthlyPayment: (values.monthlyPayment / 10000) * 100,
      annualIncome: (values.annualIncome / 500000) * 100,
      monthlyExpenses: (values.monthlyExpenses / 5000) * 100,
      downPayment: (values.downPayment / 200000) * 100
    };
  }

  updatePropertyTax(event: any) {
    this.paymentBreakdown.propertyTax = parseInt(event.target.value);
    this.calculateAffordability(true);
  }

  updateHomeInsurance(event: any) {
    this.paymentBreakdown.homeInsurance = parseInt(event.target.value);
    this.calculateAffordability(true);
  }

  calculateAffordability = (isUpdated = false) => {
    this.loading = true;
    const values = this.calculatorForm.value;

    // 1. Gross monthly income
    const grossMonthlyIncome = values.annualIncome / 12;

    // 2. Calculate back-end limit based on selected DTI percentage
    const maxTotalDebt = grossMonthlyIncome * parseFloat(this.dtiPercentage);

    // 3. Deduct existing monthly debt (monthlyExpenses) from back-end capacity
    //    to find the maximum housing payment from back-end perspective
    const maxHousingBackEnd = Math.max(0, maxTotalDebt - values.monthlyExpenses);

    // 4. Determine actual maximum housing payment 
    //    (the lesser of front-end or back-end limit)
    const maxHousingPayment = this.calculationMethod === 'dti' ? maxHousingBackEnd : values.monthlyPayment;

    // 5. Calculate the maximum loan amount using the standard mortgage formula
    const monthlyRate = values.interestRate / 100 / 12;
    const numberOfPayments = values.loanTerm.startsWith('15') ? 180 : 360;
    const factor = Math.pow(1 + monthlyRate, numberOfPayments);
    const maxLoanAmount = Math.round(
      maxHousingPayment * ((factor - 1) / (monthlyRate * factor))
    );

    // 6. Add down payment to get maximum house price
    this.maxHomePrice = Math.round(maxLoanAmount + values.downPayment);

    // 7. Calculate property tax and home insurance based on the max home price
    if (!isUpdated) {
      this.paymentBreakdown.propertyTax = Math.round((this.PROPERTY_TAX_RATE * this.maxHomePrice) / 12);
      this.paymentBreakdown.homeInsurance = Math.round((this.HOME_INSURANCE_RATE / 100) * this.maxHomePrice / 12);
    }

    // 8. Update payment breakdown
    this.paymentBreakdown.principalAndInterest = Math.round(maxHousingPayment);
    this.paymentBreakdown.monthlyExpenses = Math.round(values.monthlyExpenses);

    // 9. Calculate total monthly payment
    this.totalMonthlyPayment = Object.values(this.paymentBreakdown).reduce((a, b) => a + b, 0);
    this.loading = false;
    this.updateChartData();
  }

  updateChartData() {
    this.doughnutChartData = [
      this.paymentBreakdown.principalAndInterest,
      this.paymentBreakdown.monthlyExpenses,
      this.paymentBreakdown.propertyTax,
      this.paymentBreakdown.homeInsurance
    ];
  }

  getSliderBackground = (controlName: string): string => {
    const percentage = this.sliderPercentages[controlName] || 0;
    return `linear-gradient(to right, #ff4444 ${percentage}%, #e0e0e0 ${percentage}%)`;
  }

  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  }
}